import { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Download, Search, Filter, UserX, UserCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadSubscribers();
  }, [page, statusFilter]);

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({ page, limit: 50 });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/newsletter?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscribers(response.data.subscribers || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (subscriberId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/newsletter/${subscriberId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSubscribers();
      toast.success(`Subscriber ${status === 'active' ? 'activated' : 'unsubscribed'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportSubscribers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/newsletter/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'newsletter_subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export subscribers');
    }
  };

  const filteredSubscribers = subscribers.filter(sub => {
    if (!searchQuery) return true;
    return sub.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (sub.full_name || '').toLowerCase().includes(searchQuery.toLowerCase());
  });

  const stats = {
    total: total,
    active: subscribers.filter(s => s.status === 'active').length,
    unsubscribed: subscribers.filter(s => s.status === 'unsubscribed').length
  };

  return (
    <div className="space-y-6" data-testid="admin-newsletter">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light text-charcoal">Newsletter Subscribers</h1>
          <p className="text-charcoal-light text-sm mt-1">{total} total subscribers</p>
        </div>
        <button
          onClick={exportSubscribers}
          className="flex items-center gap-2 bg-gold text-white px-4 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
          data-testid="export-btn"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-charcoal/10 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-gold" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Total</p>
              <p className="font-heading text-2xl font-light text-charcoal">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-charcoal/10 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Active</p>
              <p className="font-heading text-2xl font-light text-green-600">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-charcoal/10 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-50 flex items-center justify-center">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Unsubscribed</p>
              <p className="font-heading text-2xl font-light text-red-600">{stats.unsubscribed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-charcoal/10 p-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full h-10 pl-10 pr-4 border border-charcoal/10 focus:border-gold outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
      </div>

      {/* Subscribers Table */}
      <div className="bg-white border border-charcoal/10 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filteredSubscribers.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-beige/50">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Email</th>
                    <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Name</th>
                    <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Source</th>
                    <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Status</th>
                    <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Subscribed</th>
                    <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal/5">
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-beige/30 transition-colors">
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal">{subscriber.email}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal">{subscriber.full_name || '-'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-xs px-2 py-1 bg-beige text-charcoal-light capitalize">
                          {subscriber.source}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-1 ${
                          subscriber.status === 'active' 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {subscriber.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal-light">
                          {new Date(subscriber.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {subscriber.status === 'active' ? (
                          <button
                            onClick={() => updateStatus(subscriber.id, 'unsubscribed')}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Unsubscribe
                          </button>
                        ) : (
                          <button
                            onClick={() => updateStatus(subscriber.id, 'active')}
                            className="text-xs text-green-600 hover:underline"
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-charcoal/10 flex items-center justify-between">
                <span className="text-sm text-charcoal-light">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-charcoal/20 text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-charcoal/20 text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-charcoal-light mb-4" />
            <h3 className="font-medium text-charcoal">No subscribers yet</h3>
            <p className="text-charcoal-light text-sm mt-1">
              Subscribers will appear here when customers sign up
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNewsletter;
